export function exportToCsv<T extends Record<string, any>>(
  data: T[],
  filename: string,
  columns: { id: keyof T; label: string; transform?: (value: any, row: T) => string }[]
) {
  if (!data || data.length === 0) {
    return;
  }

  const separator = ";"; // Excel prefers semicolon in many locales
  const BOM = "\uFEFF"; // UTF-8 BOM so Excel opens it correctly with accents

  // Header row
  const header = columns.map((col) => `"${col.label}"`).join(separator);

  // Data rows
  const rows = data.map((row) =>
    columns
      .map((col) => {
        let value: any = row[col.id];

        // Apply transformation if provided
        if (col.transform) {
          value = col.transform(value, row);
        }

        // Handle null/undefined
        if (value === null || value === undefined) {
          value = "";
        }

        // Convert to string and escape quotes
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      })
      .join(separator)
  );

  const csvContent = BOM + [header, ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToExcel<T extends Record<string, any>>(
  data: T[],
  filename: string,
  columns: {
    id: keyof T;
    label: string;
    transform?: (value: any, row: T) => string;
  }[]
) {
  import("xlsx").then((XLSX) => {
    if (!data || data.length === 0) return;

    // Transform data according to columns
    const formattedData = data.map((row) => {
      const newRow: Record<string, any> = {};
      columns.forEach((col) => {
        let value: any = row[col.id];
        if (col.transform) {
          value = col.transform(value, row);
        }
        newRow[col.label] = value;
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  }).catch((err) => {
    console.error("Failed to load xlsx library:", err);
  });
}

// Advanced Excel export with multiple sheets, formatting, and metadata
export interface ExcelSheet<T = any> {
  name: string;
  data: T[];
  columns: {
    id: keyof T;
    label: string;
    transform?: (value: any, row: T) => string | number | boolean | null;
    width?: number; // Optional column width
  }[];
  metadata?: Record<string, string | number>; // Optional metadata for this sheet
}

export interface ExcelExportOptions {
  filename: string;
  sheets: ExcelSheet[];
  globalMetadata?: Record<string, string | number>; // Global metadata (appears on first sheet)
  freezeHeader?: boolean; // Freeze first row (default: true)
  autoFilter?: boolean; // Add auto-filter (default: true)
  alternatingRows?: boolean; // Alternate row colors (default: true)
}

export function exportToExcelAdvanced(options: ExcelExportOptions) {
  import("xlsx").then((XLSX) => {
    const {
      filename,
      sheets,
      globalMetadata = {},
      freezeHeader = true,
      autoFilter = true,
      alternatingRows = true,
    } = options;

    if (!sheets || sheets.length === 0) {
      console.warn("No sheets to export");
      return;
    }

    const workbook = XLSX.utils.book_new();

    sheets.forEach((sheet, sheetIndex) => {
      if (!sheet.data || sheet.data.length === 0) {
        // Create empty sheet with headers only
        const emptyData = [
          Object.fromEntries(sheet.columns.map((col) => [col.label, ""])),
        ];
        const worksheet = XLSX.utils.json_to_sheet(emptyData);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
        return;
      }

      // Transform data according to columns
      const formattedData = sheet.data.map((row) => {
        const newRow: Record<string, any> = {};
        sheet.columns.forEach((col) => {
          let value: any = row[col.id];
          if (col.transform) {
            value = col.transform(value, row);
          }
          // Handle null/undefined
          if (value === null || value === undefined) {
            value = "";
          }
          newRow[col.label] = value;
        });
        return newRow;
      });

      // Add metadata rows if present (only on first sheet for global, or sheet-specific)
      const metadataToAdd =
        sheetIndex === 0
          ? { ...globalMetadata, ...sheet.metadata }
          : sheet.metadata;

      let rowOffset = 0;
      const metadataRows: any[] = [];

      if (metadataToAdd && Object.keys(metadataToAdd).length > 0) {
        Object.entries(metadataToAdd).forEach(([key, value]) => {
          metadataRows.push({ [sheet.columns[0].label]: key, " ": value });
        });
        // Add empty row separator
        metadataRows.push({});
        rowOffset = metadataRows.length;
      }

      // Create worksheet from data
      const allData = [...metadataRows, ...formattedData];
      const worksheet = XLSX.utils.json_to_sheet(allData, {
        skipHeader: false,
      });

      // Set column widths
      const columnWidths = sheet.columns.map((col) => ({
        wch: col.width || Math.max(col.label.length + 2, 15), // Minimum width
      }));
      worksheet["!cols"] = columnWidths;

      // Apply header row formatting (bold, background color)
      const headerRowIndex = rowOffset;
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

      // Style header row
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: headerRowIndex,
          c: col,
        });
        if (!worksheet[cellAddress]) continue;

        worksheet[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }

      // Apply alternating row colors
      if (alternatingRows) {
        for (let row = headerRowIndex + 1; row <= range.e.r; row++) {
          if ((row - headerRowIndex) % 2 === 0) {
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
              if (!worksheet[cellAddress]) continue;
              worksheet[cellAddress].s = {
                ...worksheet[cellAddress].s,
                fill: { fgColor: { rgb: "F2F2F2" } },
              };
            }
          }
        }
      }

      // Freeze header row
      if (freezeHeader && formattedData.length > 0) {
        worksheet["!freeze"] = { xSplit: 0, ySplit: headerRowIndex + 1 };
      }

      // Add auto-filter
      if (autoFilter && formattedData.length > 0) {
        const dataRange = {
          s: { r: headerRowIndex, c: 0 },
          e: { r: range.e.r, c: range.e.c },
        };
        worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(dataRange) };
      }

      // Add sheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
    });

    // Write file
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  });
}
